import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

interface LanguageSwitcherProps {
  isScrolled?: boolean;
}

export default function LanguageSwitcher({ isScrolled = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-inter text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
          isScrolled
            ? 'text-night-blue bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-voyage-gold/10 hover:border-voyage-gold shadow-sm'
            : 'text-white bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 hover:border-white/40 shadow-lg'
        }`}
      >
        <span className="text-xl leading-none">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <motion.i 
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="ri-arrow-down-s-line text-lg"
        ></motion.i>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute top-full right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl overflow-hidden z-50 border-2 border-slate-100"
          >
            <div className="p-2">
              {languages.map((lang, index) => (
                <motion.button
                  key={lang.code}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left font-inter text-sm rounded-xl transition-all duration-200 cursor-pointer group ${
                    lang.code === currentLanguage.code
                      ? 'bg-gradient-to-r from-voyage-gold/20 to-amber-100/50 text-night-blue font-bold shadow-sm'
                      : 'text-slate-700 hover:bg-slate-50 hover:shadow-sm'
                  }`}
                >
                  <span className="text-2xl leading-none transition-transform group-hover:scale-110">
                    {lang.flag}
                  </span>
                  <span className="flex-1">{lang.name}</span>
                  {lang.code === currentLanguage.code && (
                    <motion.i 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ri-check-line text-voyage-gold text-lg"
                    ></motion.i>
                  )}
                </motion.button>
              ))}
            </div>
            
            {/* Indicateur visuel en bas */}
            <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-t border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <i className="ri-global-line text-alpine-gold"></i>
                <span className="font-medium">Langue du site</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
